import React from 'react';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react';
import { Icon, mergeStyleSets, Spinner, SpinnerSize, TextField } from '@fluentui/react';
import API, { EntityAPI, IUserConfigPackage } from '../../API';
import { EntityDetailsState } from './EntityDetails';

interface IChatMessage {
	role: 'user' | 'assistant';
	text: string;
}

export class AgentChatState {
	public userConfig: IUserConfigPackage;
	public messages: IChatMessage[] = [];
	public inputText: string = '';
	public loading: boolean = false;
	entityDialog: EntityDetailsState;

	public constructor(userConfig: IUserConfigPackage, entityDialog: EntityDetailsState) {
		makeAutoObservable(this);
		this.userConfig = userConfig;
		this.entityDialog = entityDialog;
	}

	public ensureLoaded() {}

	public setInputText(value: string) {
		this.inputText = value;
	}

	public async openEntity(entityType: string, id: string) {
		const api = await EntityAPI.fromEntityTypeOrId(entityType, this.userConfig);
		const item = await api.item(id, null);
		if (item) {
			this.entityDialog.show(entityType, item);
		}
	}

	public async submit() {
		const question = this.inputText.trim();
		if (!question || this.loading) return;

		this.messages.push({ role: 'user', text: question });
		this.inputText = '';
		this.loading = true;

		try {
			const token = await this.userConfig.getToken();
			const api = new API(token);
			const answer = await api.agentQuestion(question);
			this.messages.push({ role: 'assistant', text: answer });
		} catch {
			this.messages.push({ role: 'assistant', text: 'Sorry, something went wrong. Please try again.' });
		} finally {
			this.loading = false;
		}
	}
}

export interface IAgentChatProps {
	state: AgentChatState;
}

const classNames = mergeStyleSets({
	root: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
	},
	intro: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		flex: 1,
		padding: '32px 24px 0',
		textAlign: 'center',
	},
	aiLogo: {
		width: 52,
		height: 52,
		borderRadius: '50%',
		background: 'linear-gradient(135deg, #0078d4 0%, #8764b8 100%)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginBottom: 12,
		flexShrink: 0,
	},
	introTitle: {
		fontSize: 20,
		fontWeight: 600,
		color: '#201f1e',
		margin: '0 0 6px',
	},
	introSubtitle: {
		fontSize: 14,
		color: '#605e5c',
		margin: 0,
	},
	messages: {
		flex: 1,
		overflowY: 'auto',
		padding: '16px 0',
		display: 'flex',
		flexDirection: 'column',
		gap: 12,
	},
	messageBubble: {
		maxWidth: '80%',
		padding: '10px 14px',
		borderRadius: 12,
		fontSize: 14,
		lineHeight: '1.5',
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
	},
	userBubble: {
		alignSelf: 'flex-end',
		background: '#0078d4',
		color: '#fff',
		borderBottomRightRadius: 4,
		marginRight: 16,
	},
	assistantRow: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: 10,
		marginLeft: 16,
	},
	assistantAvatar: {
		width: 28,
		height: 28,
		borderRadius: '50%',
		background: 'linear-gradient(135deg, #0078d4 0%, #8764b8 100%)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		marginTop: 2,
	},
	assistantBubble: {
		background: '#f3f2f1',
		color: '#201f1e',
		borderBottomLeftRadius: 4,
	},
	typingRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
		marginLeft: 16,
	},
	inputArea: {
		padding: '12px 16px 16px 0',
		borderTop: '1px solid #edebe9',
	},
	inputWrapper: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		border: '2px solid #0078d4',
		borderRadius: 8,
		padding: '2px 4px 2px 12px',
		background: '#fff',
		selectors: {
			'&:focus-within': {
				boxShadow: '0 0 0 2px #c7e0f4',
			},
		},
	},
	sendButton: {
		width: 30,
		height: 30,
		borderRadius: 6,
		border: 'none',
		background: '#0078d4',
		color: '#fff',
		cursor: 'pointer',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		selectors: {
			'&:hover': { background: '#106ebe' },
			'&:active': { background: '#005a9e' },
			'&:disabled': { background: '#c8c6c4', cursor: 'default' },
		},
	},
});

class AgentChat extends React.Component<IAgentChatProps> {
	private messagesEndRef = React.createRef<HTMLDivElement>();

	componentDidMount() {
		this.props.state.ensureLoaded();
	}

	componentDidUpdate() {
		this.messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	}

	render() {
		const { messages, inputText, loading } = this.props.state;
		const hasMessages = messages.length > 0;

		return (
			<div className={classNames.root}>
				{!hasMessages
					? <div>

					</div>
					: <div className={classNames.messages}>
						{messages.map((msg, i) => this.renderMessage(msg, i))}
						{loading && (
							<div className={classNames.typingRow}>
								{this.renderAssistantAvatar()}
								<Spinner size={SpinnerSize.small} />
							</div>
						)}
						<div ref={this.messagesEndRef} />
					</div>
				}

				<div className={classNames.inputArea}>
					<div className={classNames.inputWrapper}>
						<TextField
							value={inputText}
							onChange={(_, v) => this.props.state.setInputText(v || '')}
							onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); this.props.state.submit(); } }}
							placeholder="Ask a question…"
							borderless
							disabled={loading}
							styles={{
								root: { flex: 1 },
								field: { fontSize: 14, padding: '0 0 1px', height: 28, lineHeight: '28px' },
								fieldGroup: { border: 'none', background: 'transparent', height: 28 },
							}}
						/>
						<button
							title={'Send message to AI Agent'}
							className={classNames.sendButton}
							onClick={() => this.props.state.submit()}
							disabled={loading || !inputText.trim()}
						>
							<Icon iconName="Send" style={{ fontSize: 14 }} />
						</button>
					</div>
				</div>
			</div>
		);
	}

	private formatMessageText(text: string): React.ReactNode[] {
		const tokenRegex = /\[([a-zA-Z0-9-]+):([^\]]+)\]|https?:\/\/[^\s<>"]+/g;
		const parts: React.ReactNode[] = [];
		let lastIndex = 0;
		let match: RegExpExecArray | null;

		while ((match = tokenRegex.exec(text)) !== null) {
			if (match.index > lastIndex) {
				parts.push(text.slice(lastIndex, match.index));
			}

			if (match[1] && match[2]) {
				// Entity link: [entitytype:id]
				const entityType = match[1];
				const id = match[2];
				parts.push(
					<a key={match.index} href={`/entity/${entityType}/${id}`} onClick={(e) => { e.preventDefault(); this.props.state.openEntity(entityType, id); }}>
						link
					</a>
				);
			} else {
				// URL
				const url = match[0];
				const pathname = url.split('?')[0].split('#')[0];
				const lastSegment = pathname.split('/').pop() || '';
				const isFile = /\.[a-zA-Z0-9]{1,10}$/.test(lastSegment);
				const label = isFile ? lastSegment : url;
				parts.push(<a key={match.index} href={url} target="_blank" rel="noreferrer">{label}</a>);
			}

			lastIndex = match.index + match[0].length;
		}

		if (lastIndex < text.length) {
			parts.push(text.slice(lastIndex));
		}

		return parts;
	}

	private renderMessage(msg: IChatMessage, key: number) {
		if (msg.role === 'user') {
			return (
				<div key={key} className={`${classNames.messageBubble} ${classNames.userBubble}`}>
					{msg.text}
				</div>
			);
		}
		return (
			<div key={key} className={classNames.assistantRow}>
				{this.renderAssistantAvatar()}
				<div className={`${classNames.messageBubble} ${classNames.assistantBubble}`}>
					{this.formatMessageText(msg.text)}
				</div>
			</div>
		);
	}

	private renderAssistantAvatar() {
		return (
			<div className={classNames.assistantAvatar}>
				<Icon iconName="Robot" style={{ color: '#fff', fontSize: 14 }} />
			</div>
		);
	}

	private renderAiLogo(size: number) {
		return (
			<div className={classNames.aiLogo} style={{ width: size, height: size }}>
				<Icon iconName="Robot" style={{ color: '#fff', fontSize: size * 0.45 }} />
			</div>
		);
	}
}

export default observer(AgentChat);
